import React, { memo, useMemo, useRef } from "react";
import FareCardDesktop from "../../../../components/elements/FlightCard/components/FareCard.desktop";
import { t } from "@bookingcom/lingojs-core";
import { Carousel, Divider, Grid } from "@bookingcom/bui-react";
import { UIFlightData } from "@flights/types";
import { SkeletonFlightCard } from "../../../../components/elements/Skeleton/SkeletonFlightCard";
import { useI18n } from "@bookingcom/lingojs-react";
import { convertFlightToFare } from "../utils";
import useBrandedFares from "../../../hooks/useBrandedFares";
import Frame from "../../../../components/elements/Frame";
import useTrackFirstFarePriceMismatch from "../../../hooks/useTrackFirstFarePriceMismatch";
import useEqualRowHeight from "../../../../hooks/useEqualRowHeight";

type Props = {
  flight: UIFlightData;
  /* start - flights_web_flight_card_update_desktop */
  isFlightCardUpdate?: boolean;
  /* end - flights_web_flight_card_update_desktop */
};

const _FareSelectorDesktop = ({ flight, isFlightCardUpdate = false }: Props) => {
  const i18n = useI18n();
  const { fetchStatus, brandedFareOffers } = useBrandedFares(flight.token);
  const hasBrandedFareOffers = !!brandedFareOffers && brandedFareOffers?.length > 0;
  const hasPrefetchedBrandedFareOffers =
    !flight.requestableBrandedFares && !!flight.brandedFareOffers && flight.brandedFareOffers?.length > 0;
  const containerRef = useRef<HTMLDivElement>(null);

  useEqualRowHeight(containerRef, "data-fare-card-row", [brandedFareOffers, fetchStatus]);
  useTrackFirstFarePriceMismatch(flight, brandedFareOffers);

  const brandedFareOffersToRender = hasBrandedFareOffers ? brandedFareOffers : flight.brandedFareOffers;

  const hasSellableBag = useMemo(
    () =>
      brandedFareOffers &&
      brandedFareOffers.some((fare) =>
        fare?.brandedFareInfo?.sellableFeatures?.some((feature) => feature.category === "BAGGAGE")
      ),
    [brandedFareOffers]
  );

  return (
    <div ref={containerRef}>
      <Divider />
      <Frame
        direction="row"
        m={4}
        attributes={{
          style: { maxWidth: "682px" }
        }}
      >
        {hasBrandedFareOffers || hasPrefetchedBrandedFareOffers ? (
          <div style={{ width: "100%" }}>
            <Carousel
              size={"larger"}
              nextButtonAriaLabel={i18n.trans(t("a11y_flights_fare_switcher_carousel_next_button_aria_label"))}
              previousButtonAriaLabel={i18n.trans(t("a11y_flights_fare_switcher_carousel_previous_button_aria_label"))}
            >
              {brandedFareOffersToRender?.map((brandedFareOffer, i) => (
                <FareCardDesktop
                  key={brandedFareOffer.token}
                  baseOffer={flight}
                  fareOffer={brandedFareOffer}
                  index={i}
                  numberOfOffers={brandedFareOffersToRender?.length}
                  hasSellableBag={hasSellableBag}
                />
              ))}
            </Carousel>
          </div>
        ) : (
          <div style={{ width: "100%" }}>
            <Grid>
              <Grid.Column size={4}>
                <FareCardDesktop
                  fareOffer={convertFlightToFare(flight, i18n)}
                  baseOffer={flight}
                  loadingStatus={fetchStatus}
                  hasSellableBag={hasSellableBag}
                  isFlightCardUpdate={isFlightCardUpdate}
                />
              </Grid.Column>
              {fetchStatus === "loading" && (
                <Grid.Column size={4}>
                  <SkeletonFlightCard shorterCard={true} />
                </Grid.Column>
              )}
            </Grid>
          </div>
        )}
      </Frame>
    </div>
  );
};

export const FareSelectorDesktop = memo(_FareSelectorDesktop);
